import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'

/**
 * MotionPanelSecondaryBar
 * -----------------------
 * Picker for the Text Motion system (46 presets across 7 categories). Mirrors
 * AnimatePanelSecondaryBar but writes a single `item.motion` config object
 * (NOT item.animations) — motion is the BASE transform and composes under any
 * animation. Only one motion is allowed per item (toggle to remove).
 *
 * Preset ids here MUST stay in sync with the registered motion types in
 * motionEngine.js / motion_engine.py.
 */
export default function MotionPanelSecondaryBar({
  selectedElement,
  template_content,
  settemplate_content,
  onClose
}) {
  const [selectedMotion, setSelectedMotion] = useState(null)
  const [hoveredMotion, setHoveredMotion] = useState(null)
  const [appliedMotions, setAppliedMotions] = useState([])

  // Reflect the currently applied motion on the selected element.
  useEffect(() => {
    if (!selectedElement || !template_content) return
    const currentElement = template_content.pages
      ?.flatMap(page => page.items || [])
      ?.find(item => item.id === selectedElement)

    if (currentElement && currentElement.motion && currentElement.motion.type) {
      setAppliedMotions([currentElement.motion.type])
      setSelectedMotion(currentElement.motion.type)
    } else {
      setAppliedMotions([])
      setSelectedMotion(null)
    }
  }, [selectedElement, template_content])

  const handleMotionHover = (type) => setHoveredMotion(type)
  const handleMotionHoverLeave = () => setHoveredMotion(null)

  // Per-type default configs. Keys mirror the cfg fields each preset reads in
  // motionEngine.js. `loop`, `speed` and `constrain` are modifiers applied by
  // the engine core and can be layered on any preset from the properties panel.
  const getMotionDefaults = (type) => {
    const defaults = {
      // Category 1 — Basic Transform
      positionMotion: { type: 'positionMotion', easing: 'easeInOut', unit: 'px', startX: 0, startY: 0, endX: 200, endY: 0 },
      scaleMotion: { type: 'scaleMotion', easing: 'easeInOut', startScale: 1, endScale: 1.5, startScaleY: null, endScaleY: null },
      rotationMotion: { type: 'rotationMotion', easing: 'easeInOut', startAngle: 0, endAngle: 360, turns: 0 },
      opacityMotion: { type: 'opacityMotion', easing: 'easeInOut', startOpacity: 1, endOpacity: 0 },
      anchorPointMotion: { type: 'anchorPointMotion', easing: 'easeInOut', startAnchorX: 0.5, startAnchorY: 0.5, endAnchorX: 0, endAnchorY: 0 },
      letterSpacingMotion: { type: 'letterSpacingMotion', easing: 'easeInOut', startTracking: 20, endTracking: 0 },
      lineSpacingMotion: { type: 'lineSpacingMotion', easing: 'easeInOut', startLineSpacing: 0, endLineSpacing: 0.6 },

      // Category 2 — Directional & Path
      directionalTravel: { type: 'directionalTravel', easing: 'easeInOut', angle: 0, distance: 300, startX: 0, startY: 0 },
      diagonalMotion: { type: 'diagonalMotion', easing: 'easeInOut', distanceX: 300, distanceY: 200 },
      zoomTravel: { type: 'zoomTravel', easing: 'easeInOut', startScale: 0.5, endScale: 1.5, startX: 0, startY: 0, endX: 0, endY: 0 },
      orbitMotion: { type: 'orbitMotion', easing: 'linear', centerX: 0, centerY: 0, radius: 100, startAngle: 0, endAngle: 360 },
      circularMotion: { type: 'circularMotion', easing: 'linear', radius: 120, startAngle: 0, endAngle: 360, orient: false },
      followPath: { type: 'followPath', easing: 'easeInOut', path: [{ x: 0, y: 0 }, { x: 150, y: -80 }, { x: 300, y: 0 }] },
      motionPathWithStops: { type: 'motionPathWithStops', easing: 'easeInOut', points: [{ x: 0, y: 0, t: 0 }, { x: 150, y: 0, t: 0.4 }, { x: 150, y: 0, t: 0.6 }, { x: 300, y: 0, t: 1 }] },

      // Category 3 — Kinetic & Emphasis
      overshootSettle: { type: 'overshootSettle', easing: 'easeOut', property: 'position', overshoot: 0.2, startX: 0, startY: 0, endX: 200, endY: 0 },
      elasticMotion: { type: 'elasticMotion', amplitude: 0.3, frequency: 3, decay: 4, property: 'position', endX: 200, endY: 0 },
      staggeredCharMotion: { type: 'staggeredCharMotion', easing: 'easeOut', stagger: null, offsetX: 0, offsetY: 40 },
      staggeredWordMotion: { type: 'staggeredWordMotion', easing: 'easeOut', stagger: 0.12, offsetX: 0, offsetY: 40 },
      waveMotion: { type: 'waveMotion', amplitude: 12, cycles: 2, charPhase: 0.3 },
      scatterConverge: { type: 'scatterConverge', easing: 'easeOut', distance: 150, seed: 1 },
      trackingExpandCompress: { type: 'trackingExpandCompress', easing: 'easeInOut', startTracking: 0, endTracking: 30, origin: 'center' },

      // Category 4 — 3D & Perspective
      rotation3D: { type: 'rotation3D', easing: 'easeInOut', startX: 0, startY: 0, startZ: 0, endX: 0, endY: 360, endZ: 0 },
      perspectiveTilt: { type: 'perspectiveTilt', easing: 'easeInOut', horizontalTilt: 0, verticalTilt: 30 },
      depthTravel: { type: 'depthTravel', easing: 'easeInOut', startZ: 1200, endZ: 0 },
      parallaxMotion: { type: 'parallaxMotion', easing: 'easeInOut', cameraX: 200, cameraY: 0, depth: 1 },
      orbit3D: { type: 'orbit3D', easing: 'linear', centerX: 0, centerY: 0, radius: 150, startAngle: 0, endAngle: 360, facing: 'camera' },
      flipTumble3D: { type: 'flipTumble3D', easing: 'easeOut', turnsX: 0, turnsY: 1, turnsZ: 0, finalX: 0, finalY: 0, finalZ: 0 },

      // Category 5 — Path & Layout
      textOnPath: { type: 'textOnPath', path: [{ x: 0, y: 0 }, { x: 300, y: -60 }, { x: 600, y: 0 }], orient: 'path' },
      pathMorph: { type: 'pathMorph', easing: 'easeInOut', pathA: [{ x: 0, y: 0 }, { x: 300, y: 0 }], pathB: [{ x: 0, y: 0 }, { x: 150, y: -120 }, { x: 300, y: 0 }] },
      spiralMotion: { type: 'spiralMotion', easing: 'linear', centerX: 0, centerY: 0, startRadius: 0, endRadius: 150, turns: 2 },
      wavePathMotion: { type: 'wavePathMotion', easing: 'linear', distance: 600, amplitude: 40, waves: 2 },
      layoutReflow: { type: 'layoutReflow', easing: 'easeInOut', fromPositions: null, toPositions: null },

      // Category 6 — Organic & Physics
      floatingDrifting: { type: 'floatingDrifting', amplitudeX: 12, amplitudeY: 18, speed: 0.4, seed: 1 },
      springMotion: { type: 'springMotion', stiffness: 120, damping: 12, mass: 1, targetX: 100, targetY: 0 },
      pendulumSwing: { type: 'pendulumSwing', angle: 20, frequency: 0.8, decay: 0, anchorX: 0.5, anchorY: 0 },
      jitterTremble: { type: 'jitterTremble', positionAmplitude: 3, rotationAmplitude: 2, speed: 20, seed: 1 },
      impulseMotion: { type: 'impulseMotion', triggerTime: 0, strengthX: 0, strengthY: -40, stiffness: 120, damping: 10 },
      rhythmicMotion: { type: 'rhythmicMotion', beats: [0, 0.5, 1, 1.5], pulseScale: 0.15, decay: 6 },

      // Category 7 — Controls & Combinations
      keyframeInterpolation: { type: 'keyframeInterpolation', tracks: { x: [{ t: 0, value: 0 }, { t: 1, value: 200 }] } },
      speedGraph: { type: 'speedGraph', startX: 0, startY: 0, endX: 300, endY: 0, speed: { graph: [{ t: 0, v: 0 }, { t: 1, v: 1 }] } },
      motionBlur: { type: 'motionBlur', amount: 8, angle: 0, isotropic: false },
      parentLinked: { type: 'parentLinked', parentId: null },
      loopControls: { type: 'loopControls', easing: 'easeInOut', startX: 0, startY: 0, endX: 0, endY: -60, loop: { mode: 'pingpong', count: 0 } },
      constraints: { type: 'constraints', easing: 'easeInOut', startX: 0, startY: 0, endX: 400, endY: 0, constrain: { maxX: 150 } },
      perCharacterControls: { type: 'perCharacterControls', easing: 'easeOut', stagger: 0.05, chars: [] },
      presets: { type: 'presets', resolvedType: null, resolvedParams: {} },
    }
    return defaults[type]
  }

  // Picker categories. Icons reuse the shared effects sprite set; descriptions
  // are short and user-facing.
  const motionCategories = {
    'Basic Transform': [
      { type: 'positionMotion', name: 'Position', icon: 'pan.png', description: 'Move along X / Y' },
      { type: 'scaleMotion', name: 'Scale', icon: 'pop.png', description: 'Grow or shrink' },
      { type: 'rotationMotion', name: 'Rotation', icon: 'rotate.png', description: 'Spin around center' },
      { type: 'opacityMotion', name: 'Opacity', icon: 'fade.png', description: 'Fade in / out' },
      { type: 'anchorPointMotion', name: 'Anchor Point', icon: 'merge.png', description: 'Shift transform pivot' },
      { type: 'letterSpacingMotion', name: 'Letter Spacing', icon: 'spread.png', description: 'Animate tracking' },
      { type: 'lineSpacingMotion', name: 'Line Spacing', icon: 'block.png', description: 'Animate line height' },
    ],
    'Directional & Path': [
      { type: 'directionalTravel', name: 'Directional Travel', icon: 'drift.png', description: 'Travel along an angle' },
      { type: 'diagonalMotion', name: 'Diagonal', icon: 'drift.png', description: 'Move X and Y together' },
      { type: 'zoomTravel', name: 'Zoom Travel', icon: 'pop.png', description: 'Approach or recede' },
      { type: 'orbitMotion', name: 'Orbit', icon: 'rotate.png', description: 'Circle a center point' },
      { type: 'circularMotion', name: 'Circular', icon: 'rotate.png', description: 'Loop on a circle' },
      { type: 'followPath', name: 'Follow Path', icon: 'skate.png', description: 'Travel a custom curve' },
      { type: 'motionPathWithStops', name: 'Path With Stops', icon: 'skate.png', description: 'Path with timed holds' },
    ],
    'Kinetic & Emphasis': [
      { type: 'overshootSettle', name: 'Overshoot & Settle', icon: 'bounce.png', description: 'Overshoot then settle' },
      { type: 'elasticMotion', name: 'Elastic', icon: 'bounce.png', description: 'Spring oscillation' },
      { type: 'staggeredCharMotion', name: 'Staggered Chars', icon: 'ascend.png', description: 'Per-char time delay' },
      { type: 'staggeredWordMotion', name: 'Staggered Words', icon: 'block.png', description: 'Per-word time delay' },
      { type: 'waveMotion', name: 'Wave', icon: 'skate.png', description: 'Travelling char wave' },
      { type: 'scatterConverge', name: 'Scatter & Converge', icon: 'merge.png', description: 'Chars converge in' },
      { type: 'trackingExpandCompress', name: 'Tracking Expand', icon: 'spread.png', description: 'Expand / compress spacing' },
    ],
    '3D & Perspective': [
      { type: 'rotation3D', name: '3D Rotation', icon: 'rotate.png', description: 'Rotate on X / Y / Z' },
      { type: 'perspectiveTilt', name: 'Perspective Tilt', icon: 'rotate.png', description: 'Lean the text plane' },
      { type: 'depthTravel', name: 'Depth Travel', icon: 'pop.png', description: 'Move along Z' },
      { type: 'parallaxMotion', name: 'Parallax', icon: 'drift.png', description: 'Depth-based movement' },
      { type: 'orbit3D', name: '3D Orbit', icon: 'rotate.png', description: 'Orbit in 3D space' },
      { type: 'flipTumble3D', name: 'Flip & Tumble', icon: 'rotate.png', description: '3D flips then settle' },
    ],
    'Path & Layout': [
      { type: 'textOnPath', name: 'Text On Path', icon: 'skate.png', description: 'Lay chars on a curve' },
      { type: 'pathMorph', name: 'Path Morph', icon: 'skate.png', description: 'Path morphs A → B' },
      { type: 'spiralMotion', name: 'Spiral', icon: 'rotate.png', description: 'Spiral around center' },
      { type: 'wavePathMotion', name: 'Wave Path', icon: 'skate.png', description: 'Sinusoidal travel' },
      { type: 'layoutReflow', name: 'Layout Reflow', icon: 'merge.png', description: 'Reflow glyph layout' },
    ],
    'Organic & Physics': [
      { type: 'floatingDrifting', name: 'Floating Drift', icon: 'drift.png', description: 'Gentle noise drift' },
      { type: 'springMotion', name: 'Spring', icon: 'bounce.png', description: 'Damped spring to target' },
      { type: 'pendulumSwing', name: 'Pendulum', icon: 'rotate.png', description: 'Swing from a pivot' },
      { type: 'jitterTremble', name: 'Jitter & Tremble', icon: 'burst.png', description: 'Small irregular shake' },
      { type: 'impulseMotion', name: 'Impulse', icon: 'burst.png', description: 'Kick then settle' },
      { type: 'rhythmicMotion', name: 'Rhythmic', icon: 'pulse.png', description: 'Pulse on each beat' },
    ],
    'Controls & Combinations': [
      { type: 'keyframeInterpolation', name: 'Keyframes', icon: 'pan.png', description: 'Per-property keyframes' },
      { type: 'speedGraph', name: 'Speed Graph', icon: 'pan.png', description: 'Velocity remapping' },
      { type: 'motionBlur', name: 'Motion Blur', icon: 'clarify.png', description: 'Directional blur' },
      { type: 'parentLinked', name: 'Parent Linked', icon: 'merge.png', description: 'Link to a parent layer' },
      { type: 'loopControls', name: 'Loop Controls', icon: 'rotate.png', description: 'Loop / ping-pong' },
      { type: 'constraints', name: 'Constraints', icon: 'block.png', description: 'Clamp the motion range' },
      { type: 'perCharacterControls', name: 'Per-Character', icon: 'ascend.png', description: 'Explicit per-glyph' },
      { type: 'presets', name: 'Presets', icon: 'pop.png', description: 'Combined motion presets' },
    ],
  }

  const applyMotion = (type) => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items || []).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]
        const hasThis = it.motion && it.motion.type === type

        if (hasThis) {
          // Toggle off.
          delete it.motion
          setSelectedMotion(null)
        } else {
          const motion = getMotionDefaults(type)
          if (!motion) return
          // Default the motion window to the item's own time window. The engine
          // adds itemTime.start, so `start` is relative to the item.
          const time = it.time || { start: 0, duration: 2 }
          it.motion = { ...motion, start: 0, duration: time.duration || 2 }
          setSelectedMotion(type)
        }
        break
      }
    }
    settemplate_content(next)
  }

  const removeMotion = () => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items || []).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        delete p.items[idx].motion
        break
      }
    }
    settemplate_content(next)
    setSelectedMotion(null)
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Motion</h2>
        </div>
        <div className="flex items-center gap-2">
          {appliedMotions.length > 0 && (
            <button
              onClick={removeMotion}
              className="px-2 py-1 text-xs bg-red-100 text-red-600 hover:bg-red-200 rounded-md transition-colors"
            >
              Remove
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Motion Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {Object.entries(motionCategories).map(([category, motions]) => (
          <div key={category} className="mb-6">
            <h3 className="text-sm font-medium text-gray-600 mb-3 uppercase tracking-wide">
              {category}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {motions.map((motion) => {
                const isApplied = appliedMotions.includes(motion.type)
                const isHovered = hoveredMotion === motion.type
                return (
                  <button
                    key={motion.type}
                    onClick={() => applyMotion(motion.type)}
                    onMouseEnter={() => handleMotionHover(motion.type)}
                    onMouseLeave={handleMotionHoverLeave}
                    className={`group relative rounded-lg p-4 transition-all duration-200 hover:shadow-md bg-gray-50 ${
                      isApplied
                        ? 'border-2 border-green-400'
                        : isHovered
                        ? 'border-2 border-blue-400'
                        : 'border-2 border-gray-200'
                    }`}
                  >
                    {/* Icon */}
                    <div className="flex justify-center mb-2">
                      <img
                        src={`/static_resources/effects/${motion.icon}`}
                        alt={motion.name}
                        className="w-8 h-8 object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                      <div
                        className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-md hidden items-center justify-center"
                        style={{ display: 'none' }}
                      >
                        <span className="text-white text-xs font-bold">
                          {motion.name.charAt(0)}
                        </span>
                      </div>
                    </div>

                    {/* Name */}
                    <div className="text-sm font-medium text-gray-800 transition-colors">
                      {motion.name}
                    </div>

                    {/* Description */}
                    <div className="text-xs mt-1 leading-tight text-gray-500">
                      {motion.description}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-center">
          <p className="text-xs text-gray-500">
            {appliedMotions.length > 0
              ? 'Click to replace motion • Edit params in the Motion panel'
              : 'Click a motion to apply it to this text'}
          </p>
        </div>
      </div>
    </div>
  )
}
